import React, { useMemo } from "react";
import { Alert, Box, Grid, Typography } from "@mui/material";
import FunnelStageCard from "./FunnelStageCard";
import { FUNNEL_STAGES, TRANSVERSAL_STAGE, isStageConfigured } from "./funnelStages";

function pickStageMetrics(metrics, stageId) {
  const m = metrics && typeof metrics === "object" ? metrics : {};
  const stages = m.stages && typeof m.stages === "object" ? m.stages : {};
  const s = stages[stageId] && typeof stages[stageId] === "object" ? stages[stageId] : {};
  return {
    volume: s.volume ?? null,
    conversionRate: s.conversionRate ?? null,
  };
}

export default function CampaignFunnelView({ flowConfig, metrics, onOpenStage }) {
  const configuredByStage = useMemo(() => {
    const out = {};
    for (const s of FUNNEL_STAGES) out[s.id] = isStageConfigured(flowConfig, s.id);
    out[TRANSVERSAL_STAGE.id] = isStageConfigured(flowConfig, TRANSVERSAL_STAGE.id);
    return out;
  }, [flowConfig]);

  const pendingCount = useMemo(
    () =>
      [...FUNNEL_STAGES, TRANSVERSAL_STAGE].filter((s) => !configuredByStage[s.id]).length,
    [configuredByStage]
  );

  return (
    <Box>
      <Box sx={{ mb: 2.5, maxWidth: 640 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Etapas da jornada
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
          Cada card é um passo do caminho do cliente. Abra uma etapa para dizer o que acontece ali
          (canal, mensagem e objetivo).
        </Typography>
      </Box>

      {pendingCount > 0 ? (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          {pendingCount} etapa{pendingCount === 1 ? "" : "s"} ainda pendente
          {pendingCount === 1 ? "" : "s"}. Comece pelas primeiras: atrair, captar e engajar.
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 2.5 }}>
          Todas as etapas têm configuração inicial. Você pode ajustar qualquer uma a qualquer momento.
        </Alert>
      )}

      <Grid container spacing={2.5}>
        {FUNNEL_STAGES.map((s, index) => {
          const sm = pickStageMetrics(metrics, s.id);
          return (
            <Grid key={s.id} item xs={12} sm={6} lg={4}>
              <FunnelStageCard
                stepNumber={index + 1}
                label={s.label}
                description={s.description}
                example={s.example}
                configured={!!configuredByStage[s.id]}
                volume={sm.volume}
                conversionRate={sm.conversionRate}
                volumeLabel={s.volumeLabel}
                conversionLabel={s.conversionLabel}
                onClick={onOpenStage ? () => onOpenStage(s.id) : undefined}
              />
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 4, mb: 2, maxWidth: 640 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {TRANSVERSAL_STAGE.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
          {TRANSVERSAL_STAGE.description} Esta etapa pode agir em qualquer ponto da jornada, não só no
          final.
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6} lg={4}>
          <FunnelStageCard
            label={TRANSVERSAL_STAGE.label}
            description={TRANSVERSAL_STAGE.description}
            example={TRANSVERSAL_STAGE.example}
            configured={!!configuredByStage[TRANSVERSAL_STAGE.id]}
            volume={pickStageMetrics(metrics, TRANSVERSAL_STAGE.id).volume}
            conversionRate={pickStageMetrics(metrics, TRANSVERSAL_STAGE.id).conversionRate}
            volumeLabel={TRANSVERSAL_STAGE.volumeLabel}
            conversionLabel={TRANSVERSAL_STAGE.conversionLabel}
            onClick={onOpenStage ? () => onOpenStage(TRANSVERSAL_STAGE.id) : undefined}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
